import React, { useState } from 'react';
import { GENERIC_ERROR_MESSAGE, logApiError, logUnexpectedError } from '../Utils/apiError';

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/public/auth/request-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(email),
      });

      if (!response.ok) {
        await logApiError(response, 'Forgot password request failed');
        setError(
          response.status === 429
            ? 'Too many requests. Please wait a bit before trying again.'
            : GENERIC_ERROR_MESSAGE
        );
        return;
      }

      setSubmitted(true);
    } catch (err) {
      logUnexpectedError(err, 'Forgot password request failed');
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-md items-center justify-center">
        <section className="w-full rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-6">
            <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
              Meshum
            </div>
            <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
              Reset your password
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Enter the email on your account and we will send a secure reset link if the account exists.
            </p>
          </div>

          {submitted ? (
            <div className="space-y-5">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                If that email exists, a reset link has been sent.
              </div>
              <a
                href="/login"
                className="block h-12 w-full rounded-xl bg-blue-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                Back to login
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">Email</span>
                <input
                  type="email"
                  value={email}
                  placeholder="you@example.com"
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </label>

              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="h-12 w-full rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Sending...' : 'Send reset link'}
              </button>
            </form>
          )}

          <p className="mt-5 text-center text-sm text-slate-600">
            Remembered your password?{' '}
            <a href="/login" className="font-medium text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-800">
              Log in
            </a>
          </p>
        </section>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
